/**
 * 凡例カードの区分見出しの差し替え。
 *
 * 既定は種別そのまま (広域避難場所・指定緊急避難場所 → 「避難場所」) だが、
 * 自治体が別の名前で指定・案内している場所がある。既定のまま出すと、
 * 現地の看板・区の案内と地図の呼び名が食い違う。
 *
 * 実例: 北の丸公園は全国の避難先照合 (scripts/check-evacuation-freshness.mts) で
 * 唯一「区の指定緊急避難場所一覧に無い」と出た。調べると千代田区は同園を
 * **災害時退避場所 / 帰宅困難者支援場所** として案内していて、
 * 指定緊急避難場所ではない。都のマップ由来のマスターには前者の呼び名が無いため、
 * ここで補う。
 *
 * 物件ごとの上書き (DestinationV2.cardLabel) ではなく**場所の属性**として持つ。
 * 物件側に持たせると経路や下書きを取り直したときに黙って消え、
 * 「避難場所」に戻ってしまう。
 */

/** 差し替える区分見出し。CardsColumn が既定より優先して使う。 */
export interface CardLabel {
  ja: string;
  en: string;
}

/** 判定に必要な最小限の形 (マスターの EvacuationPlace から取り出す)。 */
export interface CardLabelTarget {
  name: string;
  prefecture?: string;
  municipality?: string;
}

/**
 * 差し替え表。
 *
 * 同名の施設は全国にあるので、名前だけでなく都道府県 (と分かるなら市区町村) の
 * 一致を要求する (「中央公園」は各地にある)。
 * 件数が増えるようなら出典付きのデータへ移すこと。
 */
const OVERRIDES: Array<CardLabelTarget & { label: CardLabel }> = [
  {
    // 千代田区「災害時退避場所」— 区は指定緊急避難場所として指定していない。
    // 出典: 千代田区 帰宅困難者対策 (北の丸公園は一時退避・支援の場所)。
    name: '北の丸公園',
    prefecture: '東京都',
    municipality: '千代田区',
    label: { ja: '災害時退避場所', en: 'TEMPORARY REFUGE AREA' },
  },
];

/**
 * 場所に対応する区分見出し。差し替えが無ければ undefined
 * (呼び出し側が既定の「避難場所 / EVACUATION AREA」を使う)。
 */
export function resolveCardLabel(place: CardLabelTarget): CardLabel | undefined {
  const hit = OVERRIDES.find(
    (o) =>
      o.name === place.name &&
      (!o.prefecture || o.prefecture === place.prefecture) &&
      // 市区町村は「分かるときだけ」効かせる。統合済みの東京都マスター
      // (evacuation-places.tokyo.json) は 3,661件すべてで municipality が空で、
      // 一致を必須にすると差し替えが一度も発火しない (実際にそれで嵌った)。
      // 他県のマスターは全件持っているので、そちらでは従来どおり効く。
      (!o.municipality ||
        !place.municipality ||
        o.municipality === place.municipality),
  );
  return hit ? { ...hit.label } : undefined;
}
